<script setup lang="ts">
import type { OutputBlockData, OutputData } from '@editorjs/editorjs';
import ArticleParser from '~/entities/article/ui/parser/ArticleParser.vue';

const props = defineProps<{
  format: string | null;
  targetAudience: string | null;
  description: string | null;
}>();

const parsedDescription = computed<OutputData | null>(() => {
  try {
    const parsed = props.description
      ? (JSON.parse(props.description) as OutputData)
      : null;

    return Array.isArray(parsed?.blocks) ? parsed : null;
  } catch {
    return null;
  }
});
</script>

<template>
  <div
    v-if="
      format || targetAudience || description || $slots['before-description']
    "
    :class="$style.wrapper"
  >
    <div v-if="format || targetAudience" :class="$style.info">
      <div v-if="format" :class="$style.column">
        <Icon :class="$style.icon" name="my-icon:i-format" />
        <div :class="$style.text">
          <span :class="$style.label">Формат: </span>
          <span :class="$style.value">{{ format }}</span>
        </div>
      </div>
      <div v-if="targetAudience" :class="$style.column">
        <Icon :class="$style.icon" name="my-icon:i-target" />
        <div :class="$style.text">
          <span :class="$style.label">Целевая аудитория: </span>
          <span :class="$style.value">{{ targetAudience }}</span>
        </div>
      </div>
    </div>
    <slot name="before-description" />
    <div v-if="description" :class="$style.description">
      <ArticleParser
        v-if="parsedDescription"
        :blocks="parsedDescription.blocks as OutputBlockData[]"
      />
      <div v-else :class="$style.plainText" v-html="description" />
    </div>
  </div>
</template>

<style module lang="scss">
.wrapper {
  display: flex;
  flex-direction: column;
  gap: var(--space-3x);
}

.info {
  display: flex;
  background-color: var(--color-white);
  border: 1px solid var(--color-gray-line);
}

.column {
  flex: 1;
  display: flex;
  gap: var(--space-1-5x);
  align-items: flex-start;
  padding: 16px 20px;

  & + & {
    border-left: 1px solid var(--color-gray-line);
  }
}

.icon {
  flex-shrink: 0;
  color: var(--color-red-2);
}

.text {
  @include text_body1;
  color: var(--color-black-1);
  line-height: 1.3;
}

.label {
  display: block;
  font-weight: 600;
}

.value {
  font-weight: 400;
}

.description {
  @include text_body1;
  color: var(--color-black-1);
  line-height: 1.4;

  p {
    margin: 0 0 12px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  a {
    color: var(--color-red-1);
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
}

.plainText {
  white-space: pre-line;
}

@media (max-width: 599px) {
  .text,
  .description {
    font-size: 14px;
  }
}

@media (max-width: 479px) {
  .info {
    flex-direction: column;
  }

  .column {
    & + & {
      border-left: none;
      border-top: 1px solid var(--color-gray-line);
    }
  }
}

@media (max-width: 1379px) {
  .wrapper {
    padding-left: 20px;
    padding-right: 20px;
  }
}
</style>
